import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import cx from 'clsx';
import { Icon } from '@mdi/react';
import { mdiHelpCircleOutline } from '@mdi/js';
import { AlarmCallType } from '@x-guard/xgac-types/xgac';
import { useLocale } from 'lib';
import { RS, DccStatus } from 'types';
import { procedurePrioritySelector } from 'features/details';
import { ackedSelector } from 'features/details/ack/exports';
import { conferenceStatusSelector, connectedSelector } from 'features/details/dcc';
import { phoneCallsSelector, phoneCallsStatusSelector } from 'features/details/phoneCalls';
import { staticResponseSelector } from 'features/details/response/static';
import { dynamicResponseSelector } from 'features/details/response/dynamic';
import { userContextSelector } from 'features/identity/auth';
import { useSelectProcedurePriority } from '../useProcedurePriority';
import { GuideTooltip } from './guideTooltip';
import styles from './style.module.scss';

type StepKind = 'alarmCall' | 'helperCall' | 'verify' | 'responder' | 'creator' | 'police' | 'apr' | 'aprBusy' | 'retry' | 'emergency';

type Step = { kind: StepKind; target: HTMLElement | null; name?: string };

const EMERGENCY_NUMBER = '088 126 12 50';

/** Which call the operator should join first: the alarm call, otherwise the helper call. */
const callToJoinSelector = (state: RS): 'alarmCall' | 'helperCall' | null => {

  const calls = phoneCallsSelector(state);
  const userId = userContextSelector(state)?.user?._id;
  const joined = (callId: string) => state.details.phoneCalls.participants.some((participant) => participant.alarmCall._id === callId
    && participant.ref?._ref === 'User'
    && participant.ref._id === userId
    && !!participant.state.connectedAt
    && !participant.state.disconnectedAt);
  const ofType = (type: AlarmCallType) => calls.filter(({ call }) => call.type === type);

  if (ofType(AlarmCallType.AlarmCall).length > 0) {

    return ofType(AlarmCallType.AlarmCall).some(({ call }) => !joined(call._id)) ? 'alarmCall' : null;

  }

  // Legacy conference: it is the alarm call too.
  if (conferenceStatusSelector(state) === DccStatus.Created) return connectedSelector(state) ? null : 'alarmCall';

  return ofType(AlarmCallType.HelperCall).some(({ call }) => !joined(call._id)) ? 'helperCall' : null;

};

/** Without these loaded, "nothing left to do" could simply be "not loaded yet". */
const readySelector = (state: RS) => phoneCallsStatusSelector(state).fulfilled
  && staticResponseSelector(state).status.fulfilled
  && dynamicResponseSelector(state).status.fulfilled;

const query = (target: string) => document.querySelector<HTMLElement>(`[data-guide-target="${target}"]`);
const isVisible = (el: HTMLElement) => el.offsetParent !== null;

/**
 * The next step, from state plus what is actually on screen: the answer to "who is first in the
 * calling order" is the topmost undecided row, because the response list is reversed in "help" mode
 * and hides the police group while verifying.
 */
const readStep = (
  join: 'alarmCall' | 'helperCall' | null,
  verified: boolean,
  emergency: boolean,
  keep: HTMLElement | null,
): Step => {

  if (join) return { kind: join, target: query(join) };

  if (!verified) return { kind: 'verify', target: query('priority') };

  const undecided = Array.from(document.querySelectorAll<HTMLElement>('[data-guide-undecided]')).filter(isVisible);
  const isOrganisation = (el: HTMLElement) => el.dataset.guideOrganisation !== undefined;

  // Organisations (police) wait until the mobile security patrol is done: finished, or no longer on offer.
  const aprBusy = Array.from(document.querySelectorAll<HTMLElement>('[data-guide-target="apr"]'))
    .some((el) => isVisible(el) && !el.hasAttribute('data-guide-done'));
  const open = undecided.filter((el) => !(aprBusy && isOrganisation(el)));

  // Stay on the row we already point at while it is still open: re-ranking by position while the page scrolls must not make the arrow jump around.
  const [first] = keep && open.includes(keep)
    ? [keep]
    : open.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

  if (first) {

    const kind: StepKind = first.dataset.guideCreator !== undefined ? 'creator' : 'responder';

    return { kind: first.dataset.guideTarget === 'apr' ? 'apr' : (isOrganisation(first) && 'police') || kind, target: first, name: first.dataset.guideName };

  }

  if (undecided.length > 0) return { kind: 'aprBusy', target: query('apr') };

  const anyone = Array.from(document.querySelectorAll<HTMLElement>('[data-guide-target="responder"], [data-guide-target="apr"]')).some(isVisible);

  return { kind: (!anyone || emergency) ? 'emergency' : 'retry', target: null };

};

const sameStep = (a: Step, b: Step) => a.kind === b.kind && a.target === b.target && a.name === b.name;

/** "Wat moet ik doen?" button that points an arrow at the next thing to do. */
export const ProcedureGuide: FC = () => {

  const { t } = useLocale();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const retryShown = useRef(false);
  const pointedAt = useRef<{ element: HTMLElement; priority: string }>(null);
  const [open, setOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [helperCallSkipped, setHelperCallSkipped] = useState(false);
  const [step, setStep] = useState<Step>(null);

  const acked = useSelector(ackedSelector);
  const ready = useSelector(readySelector);
  const callToJoin = useSelector(callToJoinSelector);
  const priority = useSelector(procedurePrioritySelector);
  const selectPriority = useSelectProcedurePriority();
  // The response is polled; a refetch must not switch the guide off (and close the tooltip) after the first load.
  const loadedOnce = useRef(false);

  if (ready) loadedOnce.current = true;

  // "Volgende" on the helper call tip moves on without joining it, until the tip is closed or the call is over.
  const join = callToJoin === 'helperCall' && helperCallSkipped ? null : callToJoin;

  const disabled = acked || !loadedOnce.current;
  const visible = open && !disabled;

  useEffect(() => {

    if (!visible || callToJoin !== 'helperCall') setHelperCallSkipped(false);

  }, [visible, callToJoin]);
  // Only asked while still verifying, and only once: "help" already answers it, and so does having seen it.
  const verifyDone = verified || priority === 'help';

  // Choosing the phase is the answer to the verification question.
  const previousPriority = useRef(priority);

  useEffect(() => {

    if (previousPriority.current !== priority && visible) setVerified(true);
    previousPriority.current = priority;

  }, [priority, visible]);

  useEffect(() => {

    if (!visible) return undefined;

    let frame = 0;

    const track = () => {

      frame = requestAnimationFrame(track);

      // A new phase reorders the response list: pick the top row again.
      const keep = pointedAt.current?.priority === priority ? pointedAt.current.element : null;
      const next = readStep(join, verifyDone, emergency, keep);

      pointedAt.current = next.target && ['responder', 'creator', 'police', 'apr'].includes(next.kind) ? { element: next.target, priority } : null;
      setStep((prev) => (prev && sameStep(prev, next) ? prev : next));

    };

    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);

    track();
    document.addEventListener('keydown', onKeyDown);

    return () => {

      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);

    };

  }, [visible, join, verifyDone, emergency, priority]);

  // Closing the verification tip (any way) counts as having seen it.
  useEffect(() => {

    if (!visible && step?.kind === 'verify') setVerified(true);

  }, [visible, step]);

  // Starting the thing the arrow points at (contacting someone, joining a call, ...) means the tip did its job.
  useEffect(() => {

    if (!visible || !step || !['alarmCall', 'helperCall', 'responder', 'creator', 'police', 'apr'].includes(step.kind)) return undefined;

    const onClick = (e: MouseEvent) => step.target?.contains(e.target as Node) && setOpen(false);

    document.addEventListener('click', onClick, true);

    return () => document.removeEventListener('click', onClick, true);

  }, [visible, step]);

  // Back to the retry tip as soon as there is something to do again.
  useEffect(() => {

    if (step && step.kind !== 'retry' && step.kind !== 'emergency') {

      retryShown.current = false;
      setEmergency(false);

    }

  }, [step]);

  const toggle = () => {

    if (open) return setOpen(false);

    // Asking again while the "try the earlier steps" tip was already shown: nothing is left, call X-Guard.
    if (readStep(join, verifyDone, emergency, null).kind === 'retry') {

      if (retryShown.current) setEmergency(true);
      retryShown.current = true;

    }

    return setOpen(true);

  };

  // Answering the question also switches the phase.
  const chooseVerifyAnswer = (answer: 'verification' | 'help') => {

    selectPriority(answer);
    setVerified(true);

  };

  const text = step && {
    alarmCall: t('Alarm_Detail_Response_Procedure.Guide.ConnectAlarmCall'),
    helperCall: t('Alarm_Detail_Response_Procedure.Guide.ConnectHelperCall'),
    verify: t('Alarm_Detail_Response_Procedure.Guide.Verify'),
    responder: t('Alarm_Detail_Response_Procedure.Guide.Responder', { name: step.name }),
    creator: t('Alarm_Detail_Response_Procedure.Guide.Creator', { name: step.name }),
    police: t('Alarm_Detail_Response_Procedure.Guide.Police', { name: step.name }),
    apr: t('Alarm_Detail_Response_Procedure.Guide.Apr'),
    aprBusy: t('Alarm_Detail_Response_Procedure.Guide.AprBusy'),
    retry: t('Alarm_Detail_Response_Procedure.Guide.Retry'),
    emergency: t('Alarm_Detail_Response_Procedure.Guide.Emergency'),
  }[step.kind];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={cx(styles.button, visible && styles.active)}
        onClick={toggle}
        disabled={disabled}
      >
        <Icon path={mdiHelpCircleOutline}/>
        {t('Alarm_Detail_Response_Procedure.Guide.Button')}
      </button>
      {visible && step && (
        <GuideTooltip target={step.target} fallback={buttonRef.current} onClose={() => setOpen(false)}>
          <span>{text}</span>
          {step.kind === 'emergency' && (
            <a href={`tel:${EMERGENCY_NUMBER.replace(/\s/g, '')}`}>{EMERGENCY_NUMBER}</a>
          )}
          {step.kind === 'helperCall' && (
            <button type="button" className={styles.next} onClick={() => setHelperCallSkipped(true)}>
              {t('Alarm_Detail_Response_Procedure.Guide.Next')}
            </button>
          )}
          {step.kind === 'verify' && (
            <>
              <button type="button" className={styles.next} onClick={() => chooseVerifyAnswer('verification')}>
                {t('Alarm_Detail_Response_Procedure.Guide.StillVerifying')}
              </button>
              <button type="button" className={styles.next} onClick={() => chooseVerifyAnswer('help')}>
                {t('Alarm_Detail_Response_Procedure.Guide.HelpNeeded')}
              </button>
            </>
          )}
        </GuideTooltip>
      )}
    </>
  );

};
